/**
 * MouseTooltip.jsx — Mouse-following tooltip primitive (#129).
 *
 * Foundation for replacing native `title=` attributes site-wide with a
 * themed, accessible, keyboard-focusable tooltip that follows the cursor.
 *
 * Design:
 *   • Singleton host element appended to <body> on first use — one DOM
 *     node serves all tooltips (cheap, avoids portal-per-instance).
 *   • 250ms hover-intent delay before show; instant hide on leave/scroll.
 *   • Auto-flip near viewport edges (horizontal + vertical).
 *   • Keyboard accessible — shows on focus, anchored to the focused
 *     element's bounding box (cursor position not available for keyboard).
 *
 * Usage:
 *   <Tooltip text="Explanation">
 *     <button>?</button>
 *   </Tooltip>
 *
 *   // Or spread directly onto an existing element (no extra wrapper span):
 *   <span {...tipProps('Explanation')}>...</span>
 *
 *   // Multi-line: '\n' is preserved (white-space: pre-line).
 *   <Tooltip text={`Line one\nLine two`}>...</Tooltip>
 *
 *   // Trusted rich content (key/value rows etc.):
 *   <Tooltip html="<b>Sales</b> · 12<br><b>Gold</b> · 4 200">...</Tooltip>
 */

import './MouseTooltip.css';

const SHOW_DELAY = 250;
const OFFSET_X = 14;
const OFFSET_Y = 18;
const EDGE_PAD = 6;

let host = null;
let showTimer = 0;
let pending = null; // { content, html, x, y }
let scrollBound = false;
let pinned = false; // #120 — click-to-pin state

function ensureHost() {
  if (host) return host;
  host = document.createElement('div');
  host.className = 'v2-mtip';
  host.setAttribute('role', 'tooltip');
  host.setAttribute('aria-hidden', 'true');
  document.body.appendChild(host);
  if (!scrollBound) {
    window.addEventListener('scroll', hideNow, { capture: true, passive: true });
    window.addEventListener('wheel',  hideNow, { capture: true, passive: true });
    window.addEventListener('blur',   hideNow);
    // #263 — drag-start anywhere on the page dismisses an unpinned
    // tooltip. Without this, picking up a window header while a hint
    // is showing leaves the popup floating after the window has moved
    // out from under the cursor (or been minimised). Pinned tips are
    // only dismissed when the click lands outside the host + trigger.
    document.addEventListener('pointerdown', (e) => {
      const t = e.target;
      if (pinned) {
        if (t && t.closest && (t.closest('.v2-mtip') || t.closest('[data-tip-trigger]'))) return;
        unpin();
        return;
      }
      // Unpinned: any pointerdown that isn't the trigger itself hides.
      // The trigger's own onClick re-shows on the subsequent enter.
      if (t && t.closest && (t.closest('.v2-mtip') || t.closest('[data-tip-trigger]'))) return;
      hideNow();
    }, true);
    scrollBound = true;
  }
  return host;
}

/**
 * Defence-in-depth sanitiser for the `html:` channel (#258 L1).
 *
 * No current caller passes untrusted strings, but the channel exists
 * and a future regression would yield a DOM-XSS sink. Parse the input
 * with a <template> (no script execution, no resource fetches), then
 * walk the tree and:
 *   • unwrap any element whose tag isn't in ALLOWED_TAGS (children kept)
 *   • strip any attribute that isn't in ALLOWED_ATTRS (no event handlers,
 *     no `style`, no `href`/`src`, no `srcset`, etc.)
 *
 * Allowlist intentionally minimal — matches the documented usage
 * pattern (`<b>Sales</b> · 12<br>…`) plus a few obvious siblings.
 */
const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'SPAN', 'BR', 'CODE', 'SMALL', 'DIV',
  'UL', 'OL', 'LI',
]);
const ALLOWED_ATTRS = new Set(['class']);

function safeHtml(input) {
  const tpl = document.createElement('template');
  tpl.innerHTML = String(input == null ? '' : input);
  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_ELEMENT);
  const toUnwrap = [];
  let node;
  while ((node = walker.nextNode())) {
    if (!ALLOWED_TAGS.has(node.tagName)) {
      toUnwrap.push(node);
      continue;
    }
    for (const attr of [...node.attributes]) {
      if (!ALLOWED_ATTRS.has(attr.name.toLowerCase())) {
        node.removeAttribute(attr.name);
      }
    }
  }
  for (const n of toUnwrap) {
    while (n.firstChild) n.parentNode.insertBefore(n.firstChild, n);
    n.remove();
  }
  return tpl.innerHTML;
}

let cachedW = 0;
let cachedH = 0;
let moveRaf = 0;
let moveX = 0;
let moveY = 0;

function applyContent(p) {
  const h = ensureHost();
  if (p.html != null) {
    h.innerHTML = safeHtml(p.html);
  } else {
    h.textContent = p.content == null ? '' : String(p.content);
  }
}

function position(x, y, measure = false) {
  const h = host;
  if (!h) return;
  if (measure || !cachedW || !cachedH) {
    cachedW = h.offsetWidth;
    cachedH = h.offsetHeight;
  }
  const w  = cachedW;
  const ht = cachedH;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = x + OFFSET_X;
  let top  = y + OFFSET_Y;

  // Horizontal auto-flip
  if (left + w + EDGE_PAD > vw) left = x - OFFSET_X - w;
  if (left < EDGE_PAD) left = EDGE_PAD;

  // Vertical auto-flip
  if (top + ht + EDGE_PAD > vh) top = y - OFFSET_Y - ht;
  if (top < EDGE_PAD) top = EDGE_PAD;

  h.style.left = left + 'px';
  h.style.top  = top  + 'px';
}

function reveal() {
  if (!pending) return;
  applyContent(pending);
  const h = ensureHost();
  h.classList.add('is-visible');
  h.setAttribute('aria-hidden', 'false');
  position(pending.x, pending.y, true);
}

function show(x, y, opts) {
  if (opts.content == null && opts.html == null) return;
  clearTimeout(showTimer);
  pending = { x, y, content: opts.content, html: opts.html };
  // If already visible, swap content + reposition immediately (no re-delay).
  if (host && host.classList.contains('is-visible')) {
    reveal();
    return;
  }
  showTimer = setTimeout(reveal, SHOW_DELAY);
}

function move(x, y) {
  if (pending) { pending.x = x; pending.y = y; }
  if (host && host.classList.contains('is-visible')) {
    moveX = x;
    moveY = y;
    if (!moveRaf) {
      moveRaf = requestAnimationFrame(() => {
        moveRaf = 0;
        if (host && host.classList.contains('is-visible')) {
          position(moveX, moveY, false);
        }
      });
    }
  }
}

function hideNow() {
  if (pinned) return; // pinned tooltips ignore hover/scroll dismissals
  clearTimeout(showTimer);
  showTimer = 0;
  if (moveRaf) { cancelAnimationFrame(moveRaf); moveRaf = 0; }
  pending = null;
  cachedW = 0;
  cachedH = 0;
  if (host) {
    host.classList.remove('is-visible');
    host.setAttribute('aria-hidden', 'true');
  }
}

function unpin() {
  pinned = false;
  if (host) host.classList.remove('is-pinned');
  hideNow();
}

function togglePinAt(x, y, opts) {
  if (pinned) { unpin(); return; }
  // Show immediately (no hover-intent delay) and lock.
  clearTimeout(showTimer);
  pending = { x, y, content: opts.content, html: opts.html };
  reveal();
  pinned = true;
  ensureHost().classList.add('is-pinned');
}

/** Anchor coords for keyboard-focus reveal: just below the element. */
function anchorOf(el) {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.bottom };
}

/**
 * Returns event handler props you can spread onto any element.
 *   <button {...tipProps('Refresh')}>↻</button>
 *
 * Pass `{ pin: true }` to enable click-to-pin: clicking the trigger
 * locks the tip in place (cursor-relative); clicking again or anywhere
 * outside dismisses it. Always adds the `v2-tip-trigger` class so the
 * trigger picks up the themed hover-border glow.
 */
export function tipProps(textOrOpts) {
  const opts = typeof textOrOpts === 'string'
    ? { content: textOrOpts }
    : (textOrOpts || {});
  const pinnable = opts.pin !== false; // pin-on-click is the default for #120
  return {
    'data-tip-trigger': '',
    onMouseEnter: (e) => { if (!pinned) show(e.clientX, e.clientY, opts); },
    onMouseMove:  (e) => { if (!pinned) move(e.clientX, e.clientY); },
    onMouseLeave: () => { if (!pinned) hideNow(); },
    onFocus: (e) => {
      if (pinned) return;
      const a = anchorOf(e.currentTarget);
      show(a.x, a.y, opts);
    },
    onBlur: () => { if (!pinned) hideNow(); },
    onClick: pinnable ? (e) => { e.stopPropagation(); togglePinAt(e.clientX, e.clientY, opts); } : undefined,
  };
}

/**
 * Wrapper component. Children are rendered inside a thin <span> that
 * carries the hover/focus listeners.
 *
 * Props: text (string), html (trusted rich HTML), class (extra classes
 * for the wrapper span), tag (override 'span').
 */
export default function Tooltip(props) {
  const opts = () => ({ content: props.text, html: props.html });
  const cls = () => 'v2-mtip-wrap v2-tip-trigger' + (props.class ? ' ' + props.class : '');
  const pinnable = () => props.pin !== false;

  return (
    <span
      class={cls()}
      data-tip-trigger=""
      onMouseEnter={(e) => { if (!pinned) show(e.clientX, e.clientY, opts()); }}
      onMouseMove={(e)  => { if (!pinned) move(e.clientX, e.clientY); }}
      onMouseLeave={() => { if (!pinned) hideNow(); }}
      onFocusIn={(e) => {
        if (pinned) return;
        const a = anchorOf(e.target);
        show(a.x, a.y, opts());
      }}
      onFocusOut={() => { if (!pinned) hideNow(); }}
      onClick={pinnable() ? (e) => { e.stopPropagation(); togglePinAt(e.clientX, e.clientY, opts()); } : undefined}
    >
      {props.children}
    </span>
  );
}

// Imperative escape hatch (rare — e.g. global keyboard handlers, or the
// hint system in #142 which delegates pointer events from <body>).
export const tooltip = {
  hide: hideNow,
  showAt: (x, y, opts) => show(x, y, opts || {}),
  moveTo: (x, y) => move(x, y),
  pinAt: (x, y, opts) => togglePinAt(x, y, opts || {}),
  unpin,
};
